// src/components/FeatureManagementModal.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import Modal from './Modal';
import {
    CheckCircleIcon,
    XCircleIcon,
    ArrowPathIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// Features included in each tier
const TIER_FEATURES = {
    BASIC: [
        'tender_search', 'basic_export', 'email_notifications',
        '5_saved_searches', 'document_preview', 'basic_analytics', 'single_user'
    ],
    PRO: [
        'tender_search', 'basic_export', 'email_notifications',
        '5_saved_searches', 'document_preview', 'basic_analytics', 'single_user',
        'advanced_analytics', 'api_access', 'bulk_export',
        'unlimited_saved_searches', 'team_collaboration', 'priority_support', 'custom_alerts'
    ],
    ENTERPRISE: [
        'tender_search', 'basic_export', 'email_notifications',
        '5_saved_searches', 'document_preview', 'basic_analytics', 'single_user',
        'advanced_analytics', 'api_access', 'bulk_export',
        'unlimited_saved_searches', 'team_collaboration', 'priority_support', 'custom_alerts',
        'whitelabel', 'custom_integrations', 'dedicated_support',
        'sla_guarantee', 'audit_logs', 'on_premise'
    ]
};

const getTierBadgeColor = (tier) => {
    switch (tier) {
        case 'BASIC': return 'bg-gray-600 text-gray-100';
        case 'PRO': return 'bg-blue-600 text-blue-100';
        case 'ENTERPRISE': return 'bg-purple-600 text-purple-100';
        default: return 'bg-gray-600 text-gray-100';
    }
};

const getModuleBadgeColor = (module) => {
    const colors = {
        core: 'bg-emerald-50 text-emerald-700',
        export: 'bg-cyan-50 text-cyan-700',
        notifications: 'bg-yellow-50 text-yellow-700',
        search: 'bg-blue-50 text-blue-700',
        documents: 'bg-orange-50 text-orange-700',
        analytics: 'bg-purple-50 text-purple-700',
        users: 'bg-pink-50 text-pink-700',
        integrations: 'bg-indigo-50 text-indigo-700',
        support: 'bg-green-50 text-green-700',
        branding: 'bg-red-50 text-red-700',
        compliance: 'bg-amber-50 text-amber-700',
        infrastructure: 'bg-slate-700 text-slate-300'
    };
    return colors[module] || 'bg-gray-700 text-gray-300';
};

const computeEffectiveFeatures = (catalog, tier, overrides) => {
    const overrideMap = new Map(overrides.map(o => [o.featureId, o]));
    const tierFeatures = TIER_FEATURES[tier] || TIER_FEATURES.BASIC;

    return catalog.map(feature => {
        const override = overrideMap.get(feature.id);
        const tierEnabled = tierFeatures.includes(feature.id);

        if (override) {
            return {
                ...feature,
                enabled: override.enabled,
                source: override.enabled ? 'override_grant' : 'override_revoke',
                override: {
                    id: override.id,
                    reason: override.reason,
                    createdBy: override.createdBy,
                    createdAt: override.createdAt,
                },
            };
        }

        return {
            ...feature,
            enabled: tierEnabled,
            source: 'tier',
            override: undefined,
        };
    });
};

const FeatureManagementModal = ({ isOpen, onClose, company }) => {
    const { executeQuery, executeMutation, queries, mutations } = useGraphQL();
    const [catalog, setCatalog] = useState([]);
    const [overrides, setOverrides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [actionResult, setActionResult] = useState(null);
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [reason, setReason] = useState('');

    const tier = company?.subscription?.tier || 'BASIC';

    useEffect(() => {
        if (isOpen && company) {
            loadData();
        }
    }, [isOpen, company]);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            // Load feature catalog
            const catalogData = await executeQuery(queries.getFeatureCatalog());
            setCatalog(catalogData?.featureCatalog || []);

            // Load company overrides
            const overridesData = await executeQuery(queries.getCompanyFeatureOverrides(), {
                companyId: company.id
            });
            setOverrides(overridesData?.companyFeatureOverrides || []);
        } catch (err) {
            console.error('Failed to load feature data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const effectiveFeatures = computeEffectiveFeatures(catalog, tier, overrides);

    // Group by module
    const featuresByModule = effectiveFeatures.reduce((acc, f) => {
        const module = f.module || 'other';
        if (!acc[module]) acc[module] = [];
        acc[module].push(f);
        return acc;
    }, {});

    const showActionResultMessage = (message, isError = false) => {
        setActionResult({ message, isError });
        setTimeout(() => setActionResult(null), 5000);
    };

    const handleGrantFeature = (feature) => {
        setPendingAction({ type: 'grant', feature });
        setReason('');
        setShowReasonModal(true);
    };

    const handleRevokeFeature = (feature) => {
        setPendingAction({ type: 'revoke', feature });
        setReason('');
        setShowReasonModal(true);
    };

    const handleEditOverride = (feature) => {
        setPendingAction({
            type: feature.override.enabled ? 'grant' : 'revoke',
            feature,
            isEdit: true
        });
        setReason(feature.override.reason || '');
        setShowReasonModal(true);
    };

    const handleConfirmAction = async () => {
        if (!reason.trim()) {
            showActionResultMessage('Please provide a reason for the audit trail', true);
            return;
        }

        setActionLoading(true);
        try {
            const result = await executeMutation(mutations.setFeatureOverride, {
                input: {
                    companyId: company.id,
                    featureId: pendingAction.feature.id,
                    enabled: pendingAction.type === 'grant',
                    reason: reason.trim()
                }
            });

            if (result.setFeatureOverride?.success) {
                showActionResultMessage(
                    `Successfully ${pendingAction.type === 'grant' ? 'granted' : 'revoked'} "${pendingAction.feature.name}"`
                );
                await loadData();
            } else {
                showActionResultMessage(
                    result.setFeatureOverride?.error || 'Failed to update feature override',
                    true
                );
            }
        } catch (err) {
            showActionResultMessage(err.message, true);
        } finally {
            setActionLoading(false);
            setShowReasonModal(false);
            setPendingAction(null);
            setReason('');
        }
    };

    const handleResetToDefault = async (feature) => {
        if (!confirm(`Reset "${feature.name}" to tier default?`)) {
            return;
        }

        setActionLoading(true);
        try {
            const result = await executeMutation(mutations.removeFeatureOverride, {
                companyId: company.id,
                featureId: feature.id
            });

            if (result.removeFeatureOverride?.success) {
                showActionResultMessage(`Reset "${feature.name}" to tier default`);
                await loadData();
            } else {
                showActionResultMessage(
                    result.removeFeatureOverride?.error || 'Failed to remove override',
                    true
                );
            }
        } catch (err) {
            showActionResultMessage(err.message, true);
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusBadge = (feature) => {
        if (feature.enabled) {
            return (
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-50 text-green-700">
                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                    Enabled
                </span>
            );
        }
        return (
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-700 text-gray-400">
                <XCircleIcon className="h-3 w-3 mr-1" />
                Disabled
            </span>
        );
    };

    const getSourceBadge = (feature) => {
        switch (feature.source) {
            case 'tier':
                return (
                    <span className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700">
                        Tier Default
                    </span>
                );
            case 'override_grant':
                return (
                    <span className="px-2 py-1 text-xs font-medium bg-purple-50 text-purple-700" title={feature.override?.reason}>
                        Granted
                    </span>
                );
            case 'override_revoke':
                return (
                    <span className="px-2 py-1 text-xs font-medium bg-orange-50 text-orange-700" title={feature.override?.reason}>
                        Revoked
                    </span>
                );
            default:
                return null;
        }
    };

    const renderFeatureActions = (feature) => {
        if (feature.override) {
            return (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleEditOverride(feature)}
                        className="px-2 py-1 text-xs bg-gray-600 text-white hover:bg-gray-500"
                        disabled={actionLoading}
                    >
                        Edit
                    </button>
                    <button
                        onClick={() => handleResetToDefault(feature)}
                        className="px-2 py-1 text-xs bg-gray-700 text-gray-300 hover:bg-gray-600"
                        disabled={actionLoading}
                    >
                        Reset
                    </button>
                </div>
            );
        }

        if (feature.enabled) {
            // Feature is enabled by tier - offer to revoke
            return (
                <button
                    onClick={() => handleRevokeFeature(feature)}
                    className="px-2 py-1 text-xs bg-orange-50 text-orange-700 hover:bg-orange-100"
                    disabled={actionLoading}
                >
                    Revoke
                </button>
            );
        } else {
            // Feature is disabled by tier - offer to grant
            return (
                <button
                    onClick={() => handleGrantFeature(feature)}
                    className="px-2 py-1 text-xs bg-green-50 text-green-700 hover:bg-green-100"
                    disabled={actionLoading}
                >
                    Grant
                </button>
            );
        }
    };

    if (!company) return null;

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={`Feature Access: ${company.name}`}
                size="xl"
            >
                <div className="space-y-4">
                    {/* Company tier info */}
                    <div className="bg-gray-700 p-4 flex items-center justify-between">
                        <div>
                            <span className="text-gray-300">Subscription Tier:</span>
                            <span className={`ml-2 px-3 py-1 text-sm font-medium ${getTierBadgeColor(tier)}`}>
                                {tier}
                            </span>
                        </div>
                        <div className="text-sm text-gray-400">
                            {TIER_FEATURES[tier]?.length || 0} features included by default
                        </div>
                    </div>

                    {/* Action Result */}
                    {actionResult && (
                        <div className={`${actionResult.isError ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'} border p-3`}>
                            <p className={actionResult.isError ? 'text-red-700' : 'text-green-700'}>
                                {actionResult.message}
                            </p>
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 p-3">
                            <p className="text-red-700">Error: {error}</p>
                        </div>
                    )}

                    {/* Loading */}
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
                        </div>
                    ) : (
                        <div className="space-y-6 max-h-[60vh] overflow-y-auto">
                            {Object.entries(featuresByModule).map(([module, features]) => (
                                <div key={module} className="space-y-2">
                                    <h3 className={`inline-block px-2 py-1 text-sm font-medium ${getModuleBadgeColor(module)}`}>
                                        {module.charAt(0).toUpperCase() + module.slice(1)}
                                    </h3>
                                    <div className="bg-gray-800 overflow-hidden">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="bg-gray-900">
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Feature</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Min Tier</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Source</th>
                                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-700">
                                                {features.map(feature => (
                                                    <tr
                                                        key={feature.id}
                                                        className={feature.override ? 'bg-purple-50' : ''}
                                                    >
                                                        <td className="px-4 py-3">
                                                            <div className="font-medium text-white">{feature.name}</div>
                                                            <div className="text-xs text-gray-400">{feature.description}</div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className={`px-2 py-1 text-xs ${getTierBadgeColor(feature.minTier)}`}>
                                                                {feature.minTier}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {getStatusBadge(feature)}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="space-y-1">
                                                                {getSourceBadge(feature)}
                                                                {feature.override && (
                                                                    <div className="text-xs text-gray-500">
                                                                        {feature.override.createdBy}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            {renderFeatureActions(feature)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Modal>

            {/* Reason Modal */}
            <Modal
                isOpen={showReasonModal}
                onClose={() => {
                    setShowReasonModal(false);
                    setPendingAction(null);
                    setReason('');
                }}
                title={pendingAction?.type === 'grant'
                    ? `Grant "${pendingAction?.feature?.name}"`
                    : `Revoke "${pendingAction?.feature?.name}"`}
            >
                <div className="space-y-4">
                    <div className={`p-3 ${pendingAction?.type === 'grant' ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                        <div className="flex items-start space-x-2">
                            <ExclamationTriangleIcon className={`h-5 w-5 mt-0.5 ${pendingAction?.type === 'grant' ? 'text-green-700' : 'text-orange-700'}`} />
                            <p className={`text-sm ${pendingAction?.type === 'grant' ? 'text-green-700' : 'text-orange-700'}`}>
                                {pendingAction?.type === 'grant'
                                    ? 'This will give the company access to this feature regardless of their subscription tier.'
                                    : 'This will remove the company\'s access to this feature regardless of their subscription tier.'}
                            </p>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Reason (required for audit trail)
                        </label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            rows={3}
                            placeholder="e.g., Trial extension per sales request - ticket #1234"
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            onClick={() => {
                                setShowReasonModal(false);
                                setPendingAction(null);
                                setReason('');
                            }}
                            className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-500"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirmAction}
                            disabled={actionLoading || !reason.trim()}
                            className={`px-4 py-2 text-white disabled:opacity-50 ${
 pendingAction?.type === 'grant'
 ? 'bg-green-600 hover:bg-green-500'
 : 'bg-orange-600 hover:bg-orange-500'
 }`}
                        >
                            {actionLoading ? 'Processing...' : pendingAction?.type === 'grant' ? 'Grant Access' : 'Revoke Access'}
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default FeatureManagementModal;
